import * as firebase from 'firebase/app';
import 'firebase/messaging';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';

import { FIREBASE_CONF } from '../childupp-properties';

/**
 * @implements {IChilduppNotification}
 */
export class FirebaseJsChilduppNotification {
    constructor() {
        // Setup object properties and object observables
        Object.defineProperties(this, {
            /**
             * @private
             * @property {firebase.messaging.Messaging} #fcm
             * @see https://firebase.google.com/docs/reference/js/firebase.messaging.Messaging
             */
            _fcm: {
                value: buildFirebaseAppMessaging(),
            },
            /**
             * @private
             * @type {BehaviorSubject<boolean|undefined>}
             */
            _hasPermission$: {
                value: new BehaviorSubject(undefined),
            },
            /**
             * @private
             * @type {BehaviorSubject<boolean|null|undefined>}
             */
            _token$: {
                value: new BehaviorSubject(undefined),
            },
            /**
             * @private
             * @type {Subject<Notification>}
             */
            _notifications$: {
                value: new Subject(),
            },
        });
        // Bind observables once fcm is ready
        this._fcm.then((fcm) => {
            /**
             * Retrieves the token from the fcm plugin and emits it via
             * {@link #_token$}
             * @return {void}
             */
            const notifyToken = () => {
                fcm.getToken().then((token) => {
                    if (this._token$.getValue() !== token) {
                        this._token$.next(token);
                    }
                });
            }
            // Pipe fcm token refresh to our Rx.Subject
            fcm.onTokenRefresh(() => {
                notifyToken();
            })
            // Pipe fcm notifications to our Subject
            fcm.onMessage((payload) => {
                this._notifications$.next({
                    title: payload.notification.title,
                    body: payload.notification.body,
                    icon: payload.notification.icon,
                });
            });
            // When permissions change notify a new token acordingly
            this._hasPermission$.subscribe((hasPermission) => {
                if (hasPermission === true) {
                    notifyToken();
                } else if (hasPermission === false) {
                    this._token$.next(null);
                }
            });
        });
        // If fcm initialization fails for whatever reason, emit some significative values
        this._fcm.catch((e) => {
            console.error('Couldn\'t initialzie fcm due to', e);
            this._hasPermission$.next(false);
            this._token$.next(null);
        });
    }

    /**
     * @inheritDoc
     */
    name() {
        return "FirebaseJS@" + firebase.SDK_VERSION
    }

    /**
     * @inheritDoc
     */
    requestPermission() {
        return this._fcm
        .then((fcm) => {
            return fcm.requestPermission();
        })
        .then(() => {
            this._hasPermission$.next(true);
        })
        .catch((e) => {
            this._hasPermission$.next(false);
            throw e;
        });
    }

    /**
     * @inheritDoc
     */
    hasPermission() {
        return this._hasPermission$.getValue();
    }

    /**
     * @inheritDoc
     */
    getToken() {
        return this._token$.getValue();
    }

    /**
     * @inheritDoc
     */
    onTokenRefresh(cb) {
        const subscription = this._token$.subscribe((token) => {
            cb(token);
        });
        return () => {
            subscription.unsubscribe();
        };
    }

    /**
     * @inheritDoc
     */
    onNotification(cb) {
        const subscription = this._notifications$.subscribe((notification) => {
            cb(notification);
        });
        return () => {
            subscription.unsubscribe();
        };
    }
}


/**
 * Prepares the firebase cloud messaging layer based on service workers. If the
 * initialization fails the promise will be rejected.
 *
 * @return {Promise<firebase.messaging.Messaging>}
 * @see https://firebase.google.com/docs/reference/js/firebase.messaging.Messaging
 */
function buildFirebaseAppMessaging() {
    return Promise.resolve(navigator && ('serviceWorker' in navigator))
    .then((hasServiceWorker) => {
        if (!hasServiceWorker) {
            const err = new Error('navigator doesn\'t support serviceWorkers');
            return Promise.reject(err);
        }
        return FIREBASE_CONF;
    })
    .then((conf) => {
        if (!conf) {
            const err = new Error('No firebase config provided');
            return Promise.reject(err);
        }
        return firebase.initializeApp(conf).messaging()
    })
    .then((fcm) => {
        return Promise.all([
            navigator.serviceWorker.ready,
            fcm,
        ])
    })
    .then(([ serviceWorkerRegistration, fcm ]) => {
        fcm.useServiceWorker(serviceWorkerRegistration);
        return fcm;
    })
}



// WEBPACK FOOTER //
// ./src/shared/childupp-notifications/firebasejs-childupp-notification.js